/// <amd-module name="Feed/Legacy/Components/Settings/UpstreamSettingsList" />
import * as React from "react";

import { IconButton } from "OfficeFabric/Button";
import {
    CheckboxVisibility,
    ColumnActionsMode,
    ConstrainMode,
    DetailsListLayoutMode,
    DetailsRow,
    IDetailsRowProps
} from "OfficeFabric/DetailsList";
import { IObjectWithKey, Selection, SelectionMode } from "OfficeFabric/Selection";

import * as Utils_String from "VSS/Core/Util/String";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { IPageData, IVssPageService } from "VSS/Platform/Page";

import { Link } from "VSSUI/Link";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { IStatusProps, Status, Statuses, StatusSize } from "VSSUI/Status";
import { TooltipHost, TooltipOverflowMode } from "VSSUI/Tooltip";
import * as Utils_Date from "VSSUI/Utilities/Date";
import { VssDetailsList } from "VSSUI/VssDetailsList";
import { VssIcon } from "VSSUI/VssIcon";

import { UpstreamSource } from "Feed/Common/Contracts/Feed";
import { UpstreamHealthStatus, UpstreamStatusCategory } from "Feed/Common/Contracts/WebPage";
import * as PackageResources from "Feed/Common/Resources";
import { BowtieIconProps } from "Feed/Common/Utils/Icons";
import { FeedSettingsActionCreator } from "Feed/Legacy/Actions/FeedSettingsActionCreator";
import { IUpstreamHealthStatusDialogDetails } from "Feed/Legacy/Common/ActionPayloads";
import { getAuditLogUrl, getPermissionSettingsUrl } from "Feed/Legacy/Helpers/UrlHelper";
import { MavenPackageProtocol } from "Feed/Legacy/Protocols/Maven/MavenPackageProtocol";
import { NpmPackageProtocol } from "Feed/Legacy/Protocols/Npm/NpmPackageProtocol";
import { NuGetPackageProtocol } from "Feed/Legacy/Protocols/NuGet/NuGetPackageProtocol";
import { PyPiPackageProtocol } from "Feed/Legacy/Protocols/PyPi/PyPiPackageProtocol";
import { IUpstreamSettingsRowData } from "Feed/Legacy/Types/IUpstreamSettingsRowData";
import { Feed } from "Feed/Legacy/WebApi/VSS.CustomFeed.Contracts";

export interface IUpstreamSettingsListProps {
    pageContext: IVssPageContext;
    feed: Feed;

    /**
     * The rows to display
     */
    upstreamSourceRows: IUpstreamSettingsRowData[];

    /**
     * True if the current user can make changes to the feed settings
     */
    isUserAdmin: boolean;

    /**
     * Tracks if views are getting saved to server
     */
    isSavingChanges: boolean;
}

export class UpstreamSettingsList extends VssComponent<IUpstreamSettingsListProps> {
    private selection: Selection;
    private warningIcon = VssIcon.getIconProps(new BowtieIconProps("Warning"));
    private originalUpstreamCount: number;

    constructor(props: IUpstreamSettingsListProps, context: IVssComponentContext) {
        super(props);

        this.selection = new Selection({
            canSelectItem: (item: IObjectWithKey): boolean => {
                if (props.isUserAdmin === false) {
                    return false;
                }

                if (props.isSavingChanges === true) {
                    return false;
                }

                return true;
            },
            getKey: (item: IObjectWithKey): string => {
                const upstreamSettingsRowData: IUpstreamSettingsRowData = item as IUpstreamSettingsRowData;
                return upstreamSettingsRowData.upstreamSource.id;
            },
            onSelectionChanged: (): void => {
                const upstreamSettingsRowData = this.selection.getSelection() as IUpstreamSettingsRowData[];
                const sources: UpstreamSource[] = upstreamSettingsRowData.map(
                    (rowData: IUpstreamSettingsRowData) => rowData.upstreamSource
                );
                FeedSettingsActionCreator.onUpstreamSourcesSelectionChanged.invoke(sources);
            }
        });

        this.originalUpstreamCount = props.upstreamSourceRows.length;
    }

    public render(): JSX.Element {
        return (
            <VssDetailsList
                setKey="upstreamsources-grid"
                className="upstream-settings-list"
                constrainMode={ConstrainMode.unconstrained}
                layoutMode={DetailsListLayoutMode.justified}
                items={this.props.upstreamSourceRows}
                selectionMode={SelectionMode.multiple}
                selectionPreservedOnEmptyClick={true}
                selection={this.selection}
                checkboxVisibility={CheckboxVisibility.onHover}
                ariaLabel={PackageResources.FeedSettings_Upstreams_Grid_AriaLabel}
                ariaLabelForGrid={PackageResources.FeedSettings_Upstreams_Grid_AriaLabelForGrid}
                ariaLabelForListHeader={PackageResources.FeedSettings_Upstreams_Grid_AriaLabelForHeader}
                ariaLabelForSelectionColumn={PackageResources.DetailsList_SelectionColumn_AriaLabel}
                checkButtonAriaLabel={PackageResources.DetailsList_Select_AriaLabel}
                ariaLabelForSelectAllCheckbox={PackageResources.DetailsList_SelectAll_AriaLabel}
                allocateSpaceForActionsButtonWhileHidden={false}
                onRenderRow={this.onRenderRow}
                columns={this.getColumns()}
            />
        );
    }

    private onRenderRow(props: IDetailsRowProps): JSX.Element {
        const rowClassName = "upstream-source-row" + (props.item.isInvalid ? " is-disabled" : "");
        return <DetailsRow className={rowClassName} {...props} />;
    }

    private getColumns() {
        let columns = [
            {
                key: "protocol",
                fieldName: "protocol",
                name: PackageResources.UpstreamSettingsList_ProtocolColumnName,
                iconName: "Page",
                isIconOnly: true,
                columnActionsMode: ColumnActionsMode.disabled,
                onRender: (item: IUpstreamSettingsRowData) => {
                    const { protocolName, iconProps } = item;
                    return (
                        <label className="settings-grid-cell" aria-label={protocolName}>
                            <TooltipHost content={protocolName}>
                                <VssIcon {...iconProps} />
                            </TooltipHost>
                        </label>
                    );
                },
                minWidth: 16,
                maxWidth: 16,
                isResizable: false
            },
            {
                key: "source",
                fieldName: "source",
                name: PackageResources.UpstreamSourceColumn,
                className: "string-column",
                columnActionsMode: ColumnActionsMode.disabled,
                onRender: (item: IUpstreamSettingsRowData) => {
                    return (
                        <TooltipHost content={item.upstreamSource.name} overflowMode={TooltipOverflowMode.Parent}>
                            <label className="settings-grid-cell">{item.upstreamSource.name}</label>
                        </TooltipHost>
                    );
                },
                minWidth: 100,
                maxWidth: 200,
                isResizable: true
            },
            {
                key: "location",
                fieldName: "location",
                name: PackageResources.UpstreamSettingsList_LocationColumnName,
                className: "string-column",
                columnActionsMode: ColumnActionsMode.disabled,
                onRender: (item: IUpstreamSettingsRowData) => {
                    // Prefer DisplayLocation (which may or may not exist),
                    // since for internal upstreams Location now always has IDs
                    const location = item.upstreamSource.displayLocation || item.upstreamSource.location;
                    return (
                        <TooltipHost content={location} overflowMode={TooltipOverflowMode.Parent}>
                            <label className="settings-grid-cell">{location}</label>
                        </TooltipHost>
                    );
                },
                minWidth: 200,
                maxWidth: 350,
                isResizable: true
            },
            {
                key: "sourceType",
                fieldName: "sourceType",
                name: PackageResources.UpstreamSettingsList_SourceTypeColumnName,
                className: "string-column",
                columnActionsMode: ColumnActionsMode.disabled,
                onRender: (item: IUpstreamSettingsRowData) => {
                    const warningMessage: string = Utils_String.format(
                        PackageResources.UpstreamSettingsList_DisabledTooltip,
                        item.protocolName
                    );

                    return (
                        <label className="settings-grid-cell">
                            {item.upstreamSourceType}
                            {item.isInvalid ? (
                                <TooltipHost content={warningMessage}>
                                    <IconButton
                                        className={"edit-warning"}
                                        iconProps={this.warningIcon}
                                        ariaLabel={warningMessage}
                                    />
                                </TooltipHost>
                            ) : null}
                        </label>
                    );
                },
                minWidth: 100,
                maxWidth: 150
            }
        ];

        columns.push({
            key: "healthStatus",
            fieldName: "healthStatus",
            name: PackageResources.FeedSettings_UpstreamStatus_ColumnName,
            className: "string-column",
            columnActionsMode: ColumnActionsMode.disabled,
            onRender: (item: IUpstreamSettingsRowData) => {
                return this.getUpstreamStatusElement(item);
            },
            minWidth: 30,
            maxWidth: 120
        });

        return columns;
    }

    private getUpstreamStatusElement(item: IUpstreamSettingsRowData): JSX.Element {
        const currentStatus = item.upstreamHealthStatus;
        if (!currentStatus) {
            if (this.originalUpstreamCount < this.props.upstreamSourceRows.length) {
                // If a new upstream was added, instead of the spinner, show the unknown status
                return this.getUnknownStatus(item.upstreamSource.id);
            }
            return (
                <div className="upstream-status upstream-status-spinner-container">
                    <Spinner
                        ariaLabel={PackageResources.FeedSettings_UpstreamStatus_Loading}
                        size={SpinnerSize.xSmall}
                        className="upstream-status-spinner"
                    />
                </div>
            );
        }

        // Partial status can only be populated if the date is newer than confident status
        const statusDate =
            currentStatus.confidentStatus != null && currentStatus.confidentStatus.timeStamp != null
                ? Utils_Date.ago(currentStatus.confidentStatus.timeStamp, Utils_Date.AgoFormat.Extended)
                : currentStatus.partialStatus != null && currentStatus.partialStatus.timeStamp != null
                    ? Utils_Date.ago(currentStatus.partialStatus.timeStamp)
                    : null;
        if (statusDate == null) {
            return this.getUnknownStatus(currentStatus.upstreamId);
        }

        const statusProps: IStatusProps = this.getStatusPropsFromStatus(currentStatus);
        if (statusProps === Statuses.Success) {
            return (
                <TooltipHost content={PackageResources.FeedSettings_UpstreamStatus_Success}>
                    <Status
                        {...statusProps}
                        key={currentStatus.upstreamId}
                        size={StatusSize.s}
                        className="upstream-status"
                    />
                    {statusDate}
                </TooltipHost>
            );
        }

        return (
            <Link
                onClick={() =>
                    this.onStatusFailureLinkClick(currentStatus, statusProps, item.upstreamSource, item.protocolName)
                }
                role="button"
                href="#"
                rel="noopener noreferrer"
                tooltipProps={{ text: PackageResources.FeedSettings_UpstreamStatus_Failure }}
                ariaLabel={PackageResources.FeedSettings_UpstreamStatus_Failure}
            >
                <Status
                    {...statusProps}
                    key={currentStatus.upstreamId}
                    size={StatusSize.s}
                    className="upstream-status"
                />
                {statusDate}
            </Link>
        );
    }

    private getUnknownStatus(key: string): JSX.Element {
        return (
            <TooltipHost content={PackageResources.FeedSettings_UpstreamStatus_Unknown}>
                <Status {...Statuses.Information} key={key} size={StatusSize.s} className="upstream-status" />
                {PackageResources.FeedSettings_UpstreamStatus_UnknownSyncStatus}
            </TooltipHost>
        );
    }

    private getStatusPropsFromStatus(currentStatus: UpstreamHealthStatus): IStatusProps {
        if (currentStatus.partialStatus != null && currentStatus.partialStatus.statusCategory != null) {
            return Statuses.Warning;
        } else if (
            currentStatus.confidentStatus &&
            currentStatus.confidentStatus.statusCategory == UpstreamStatusCategory.FullRefreshSuccess
        ) {
            return Statuses.Success;
        } else {
            return Statuses.Failed;
        }
    }

    private onStatusFailureLinkClick(
        currentStatus: UpstreamHealthStatus,
        statusProps: IStatusProps,
        upstream: UpstreamSource,
        protocol: string
    ): void {
        let dialogProps: IUpstreamHealthStatusDialogDetails;
        if (statusProps == Statuses.Warning && currentStatus.partialStatus != null) {
            dialogProps = this.createHealthStatusDialog(currentStatus.partialStatus.statusCategory, protocol, upstream);
        } else if (statusProps == Statuses.Failed && currentStatus.confidentStatus != null) {
            dialogProps = this.createHealthStatusDialog(
                currentStatus.confidentStatus.statusCategory,
                protocol,
                upstream
            );
        }

        if (dialogProps) {
            dialogProps.upstreamId = upstream.id;
            FeedSettingsActionCreator.openUpstreamStatusDialog.invoke(dialogProps);
        }
    }

    private createHealthStatusDialog(
        category: UpstreamStatusCategory,
        protocol: string,
        upstream: UpstreamSource
    ): IUpstreamHealthStatusDialogDetails {
        const pageData: IPageData = this.props.pageContext.getService<IVssPageService>("IVssPageService").getData();
        const isSameOrg = Utils_String.equals(pageData.hostId, upstream.internalUpstreamCollectionId, true);
        switch (category) {
            case UpstreamStatusCategory.TargetFeedDeleted:
                return {
                    headerText: PackageResources.FeedSettings_UpstreamStatus_Dialog_TargetFeedDeleted_Heading,
                    dialogBody: (
                        <div className="upstream-status-dialog-content">
                            {PackageResources.FeedSettings_UpstreamStatus_Dialog_TargetFeedDeleted_Message}
                            <div className="upstream-status-attention">
                                {PackageResources.FeedSettings_UpstreamStatus_Dialog_ActionHeading}
                            </div>
                            {this.getAuditLogLinkMessage(isSameOrg)}
                            {this.getFeedPermissionsLinkMessage(upstream, isSameOrg)}
                            {this.getReadMoreLinkMessage()}
                        </div>
                    ),
                    warningBody: this.getWarningMessage()
                };
            case UpstreamStatusCategory.TargetViewDeleted:
                return {
                    headerText: PackageResources.FeedSettings_UpstreamStatus_Dialog_TargetViewDeleted_Heading,
                    dialogBody: (
                        <div className="upstream-status-dialog-content">
                            {PackageResources.FeedSettings_UpstreamStatus_Dialog_TargetViewDeleted_Message}
                            <div className="upstream-status-attention">
                                {PackageResources.FeedSettings_UpstreamStatus_Dialog_ActionHeading}
                            </div>
                            {this.getAuditLogLinkMessage(isSameOrg)}
                            {this.getFeedPermissionsLinkMessage(upstream, isSameOrg)}
                            {this.getReadMoreLinkMessage()}
                        </div>
                    ),
                    warningBody: this.getWarningMessage()
                };
            case UpstreamStatusCategory.TargetProjectDeleted:
                return {
                    headerText: PackageResources.FeedSettings_UpstreamStatus_Dialog_TargetProjectDeleted_Heading,
                    dialogBody: (
                        <div className="upstream-status-dialog-content">
                            {PackageResources.FeedSettings_UpstreamStatus_Dialog_TargetProjectDeleted_Message}
                            <div className="upstream-status-attention">
                                {PackageResources.FeedSettings_UpstreamStatus_Dialog_ActionHeading}
                            </div>
                            {this.getAuditLogLinkMessage(isSameOrg)}
                            {this.getReadMoreLinkMessage()}
                        </div>
                    ),
                    warningBody: this.getWarningMessage()
                };
            case UpstreamStatusCategory.TargetViewInsufficientVisibility:
                return {
                    headerText:
                        PackageResources.FeedSettings_UpstreamStatus_Dialog_TargetViewInsufficientVisibility_Heading,
                    dialogBody: (
                        <div className="upstream-status-dialog-content">
                            {
                                PackageResources.FeedSettings_UpstreamStatus_Dialog_TargetViewInsufficientVisibility_Message
                            }
                            <div className="upstream-status-attention">
                                {PackageResources.FeedSettings_UpstreamStatus_Dialog_ActionHeading}
                            </div>
                            {this.getAuditLogLinkMessage(isSameOrg)}
                            {this.getFeedPermissionsLinkMessage(upstream, isSameOrg)}
                            {this.getReadMoreLinkMessage()}
                        </div>
                    ),
                    warningBody: this.getWarningMessage()
                };
            case UpstreamStatusCategory.PublicUpstreamFailure:
                let publicStatusLink: string;
                switch (protocol) {
                    case NuGetPackageProtocol.name:
                        publicStatusLink = "https://status.nuget.org/";
                        break;
                    case NpmPackageProtocol.name:
                        publicStatusLink = "https://status.npmjs.org/";
                        break;
                    case MavenPackageProtocol.name:
                        publicStatusLink = "https://status.maven.org/";
                        break;
                    case PyPiPackageProtocol.name:
                        publicStatusLink = "https://status.python.org/";
                        break;
                    default:
                        publicStatusLink = null;
                        break;
                }
                return {
                    headerText: PackageResources.FeedSettings_UpstreamStatus_Dialog_PublicUpstreamFailure_Heading,
                    dialogBody: (
                        <div className="upstream-status-dialog-content">
                            <div>
                                {PackageResources.FeedSettings_UpstreamStatus_Dialog_PublicUpstreamFailure_Message}
                            </div>
                            <Link href={publicStatusLink}>{publicStatusLink}</Link>
                            {this.getReadMoreLinkMessage()}
                        </div>
                    )
                };
            case UpstreamStatusCategory.CustomPublicUpstreamFailure:
                return {
                    headerText: PackageResources.FeedSettings_UpstreamStatus_Dialog_CustomPublicUpstreamFailure_Heading,
                    dialogBody: (
                        <div className="upstream-status-dialog-content">
                            {PackageResources.FeedSettings_UpstreamStatus_Dialog_CustomPublicUpstreamFailure_Message}
                            {this.getReadMoreLinkMessage()}
                        </div>
                    ),
                    warningBody: this.getWarningMessage()
                };
            case UpstreamStatusCategory.CustomPublicUpstreamFailure_DuplicatePackageVersions:
                return {
                    headerText: PackageResources.FeedSettings_UpstreamStatus_Dialog_CustomPublicUpstreamFailure_Heading,
                    dialogBody: (
                        <div className="upstream-status-dialog-content">
                            {PackageResources.FeedSettings_UpstreamStatus_Dialog_CustomPublicUpstreamFailureDuplicatePackageVersion_Message}
                            {this.getReadMoreLinkMessage()}
                        </div>
                    ),
                    warningBody: this.getWarningMessage()
                };
            case UpstreamStatusCategory.UnknownFailure:
                return {
                    headerText: PackageResources.FeedSettings_UpstreamStatus_Dialog_UnknownFailure_Heading,
                    dialogBody: (
                        <div className="upstream-status-dialog-content">
                            {PackageResources.FeedSettings_UpstreamStatus_Dialog_UnknownFailure_Message}
                            {this.getReadMoreLinkMessage()}
                        </div>
                    )
                };
            case UpstreamStatusCategory.TargetOrganizationInaccessible:
                return {
                    headerText: PackageResources.FeedSettings_UpstreamStatus_Dialog_TargetOrganizationInaccessible_Heading,
                    dialogBody: (
                        <div className="upstream-status-dialog-content">
                            {PackageResources.FeedSettings_UpstreamStatus_Dialog_TargetOrganizationInaccessible_Message}
                            {this.getReadMoreLinkMessage()}
                        </div>
                    ),
                    warningBody: this.getWarningMessage()
                };
            case UpstreamStatusCategory.TargetOrganizationServiceConnectionFailure:
                return {
                    headerText: PackageResources.FeedSettings_UpstreamStatus_Dialog_TargetOrganizationServiceConnectionFailure_Heading,
                    dialogBody: (
                    <div className="upstream-status-dialog-content">
                        {PackageResources.FeedSettings_UpstreamStatus_Dialog_TargetOrganizationServiceConnectionFailure_Message}
                        {this.getReadMoreLinkMessage()}
                    </div>
                    ),
                    warningBody: this.getWarningMessage()
                };
            default:
                return null;
        }
    }

    private getWarningMessage(): JSX.Element {
        return (
            <FormatComponent format={"{0} {1}"}>
                {PackageResources.FeedSettings_UpstreamStatus_Dialog_WarningMessage_UserAction}
                {PackageResources.FeedSettings_UpstreamStatus_Dialog_WarningMessage}
            </FormatComponent>
        );
    }

    private getAuditLogLinkMessage(isSameOrg: boolean) {
        // For the same org, we'll give a link to the audit log, for a different org, we'll give the link to audit log docs.
        const url = isSameOrg
            ? getAuditLogUrl(this.props.pageContext)
            : "https://docs.microsoft.com/en-us/azure/devops/organizations/settings/azure-devops-auditing";

        return (
            <div className="dialog-link-area">
                <FormatComponent format={PackageResources.FeedSettings_UpstreamStatus_Dialog_AuditlogMessage}>
                    <Link href={url}>{PackageResources.FeedSettings_UpstreamStatus_Dialog_AuditlogLink}</Link>
                </FormatComponent>
            </div>
        );
    }

    private getFeedPermissionsLinkMessage(upstream: UpstreamSource, isSameOrg: boolean): JSX.Element {
        const url = isSameOrg
            ? getPermissionSettingsUrl(this.props.pageContext, upstream.internalUpstreamFeedId)
            : null;

        return (
            <div className="dialog-link-area">
                <FormatComponent format={PackageResources.FeedSettings_UpstreamStatus_Dialog_PermissionMessage}>
                    {url ? (
                        <Link href={url}>{PackageResources.FeedSettings_UpstreamStatus_Dialog_PermissionLink}</Link>
                    ) : (
                            PackageResources.FeedSettings_UpstreamStatus_Dialog_PermissionLink
                        )}
                </FormatComponent>
            </div>
        );
    }

    private getReadMoreLinkMessage() {
        return (
            <div className="dialog-link-area read-more-link-area">
                <FormatComponent format={PackageResources.FeedSettings_UpstreamStatus_Dialog_DocumentationMessage}>
                    <Link
                        href={
                            "https://go.microsoft.com/fwlink/?linkid=2137532"
                        }
                    >
                        {PackageResources.FeedSettings_UpstreamStatus_Dialog_DocumentationLink}
                    </Link>
                </FormatComponent>
            </div>
        );
    }
}
